import { inject } from '@angular/core';
import { Routes } from '@angular/router';

import { environment } from '../environments/environment';

import { i18nGuard } from './core/i18n';
import {
  AuthGuard,
  eventEditableGuard,
  tenantSetupGuard,
  tenantWritableGuard,
  twoFactorSetupGuard,
} from './shared/guards';
import { AuthStateService } from './shared/service';

const TITLE = 'ERRORS.DOCUMENT_TITLES';

const settingsLandingTab = (): string =>
  inject(AuthStateService).isTenantSetupComplete() ? 'tenant' : 'account';

const simpleLayoutRoutes: Routes = [
  {
    path: '',
    canActivate: [i18nGuard],
    data: { i18n: ['welcome-root'] },
    title: `${TITLE}.LOGIN`,
    loadComponent: async () =>
      import('./pages/welcome-root/welcome-root.component').then(
        (m) => m.WelcomeRootComponent,
      ),
  },
  {
    path: 'auth/reset-password',
    canActivate: [i18nGuard],
    data: { i18n: ['welcome-root'] },
    title: `${TITLE}.RESET_PASSWORD`,
    loadComponent: async () =>
      import('./pages/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
  },
  {
    path: 'auth/two-factor-setup',
    canActivate: [AuthGuard, twoFactorSetupGuard, i18nGuard],
    data: { i18n: ['two-factor-setup'] },
    title: `${TITLE}.TWO_FACTOR_SETUP`,
    loadComponent: async () =>
      import('./pages/two-factor-setup/two-factor-setup.component').then(
        (m) => m.TwoFactorSetupComponent,
      ),
  },
  {
    path: 'reactivate',
    canActivate: [i18nGuard],
    data: { i18n: ['account-reactivate'] },
    title: `${TITLE}.ACCOUNT_REACTIVATE`,
    loadComponent: async () =>
      import('./pages/account-reactivate/account-reactivate.component').then(
        (m) => m.AccountReactivateComponent,
      ),
  },
  {
    path: 'security-alert',
    canActivate: [i18nGuard],
    data: { i18n: ['security-alert'] },
    title: `${TITLE}.SECURITY_ALERT`,
    loadComponent: async () =>
      import('./pages/security-alert/security-alert.component').then(
        (m) => m.SecurityAlertComponent,
      ),
  },
  {
    path: 'scanner/pair/:payload',
    canActivate: [i18nGuard],
    data: { i18n: ['scanner-pair'] },
    title: `${TITLE}.SCANNER_PAIR`,
    loadComponent: async () =>
      import('./pages/scanner-pair/scanner-pair.component').then(
        (m) => m.ScannerPairComponent,
      ),
  },
  {
    path: 'imprint',
    canActivate: [i18nGuard],
    data: { i18n: ['legal'], page: 'imprint' },
    title: `${TITLE}.IMPRINT`,
    loadComponent: async () =>
      import('./pages/legal-root/legal-page.component').then(
        (m) => m.LegalPageComponent,
      ),
  },
  {
    path: 'terms',
    canActivate: [i18nGuard],
    data: { i18n: ['legal'], page: 'terms' },
    title: `${TITLE}.TERMS`,
    loadComponent: async () =>
      import('./pages/legal-root/legal-page.component').then(
        (m) => m.LegalPageComponent,
      ),
  },
  {
    path: 'privacy',
    canActivate: [i18nGuard],
    data: { i18n: ['legal'], page: 'privacy' },
    title: `${TITLE}.PRIVACY`,
    loadComponent: async () =>
      import('./pages/legal-root/legal-page.component').then(
        (m) => m.LegalPageComponent,
      ),
  },
];

const onboardingRoutes: Routes = [
  {
    path: 'onboarding/tenant-setup',
    canActivate: [AuthGuard, twoFactorSetupGuard, tenantSetupGuard, i18nGuard],
    data: { i18n: ['onboarding', 'welcome-root'] },
    title: `${TITLE}.TENANT_SETUP`,
    loadComponent: async () =>
      import('./pages/onboarding/tenant-setup/tenant-setup.component').then(
        (m) => m.TenantSetupComponent,
      ),
  },
  {
    path: 'onboarding/mollie/callback',
    canActivate: [AuthGuard, twoFactorSetupGuard, i18nGuard],
    data: { i18n: ['onboarding'] },
    title: `${TITLE}.MOLLIE_CALLBACK`,
    loadComponent: async () =>
      import('./pages/onboarding/mollie-callback/mollie-callback.component').then(
        (m) => m.MollieCallbackComponent,
      ),
  },
];

const protectedRoutes: Routes = [
  {
    path: 'dashboard',
    canActivate: [tenantSetupGuard, i18nGuard],
    data: { i18n: ['dashboard', 'onboarding', 'event-root', 'floor-types'] },
    title: `${TITLE}.DASHBOARD`,
    loadComponent: async () =>
      import('./pages/dashboard-root/dashboard-root.component').then(
        (m) => m.DashboardRootComponent,
      ),
  },
  {
    path: 'storefront',
    canActivate: [tenantSetupGuard, i18nGuard],
    data: { i18n: ['storefront'] },
    loadComponent: async () =>
      import('./pages/storefront-root/storefront-root.component').then(
        (m) => m.StorefrontRootComponent,
      ),
    children: [
      { path: '', redirectTo: 'general', pathMatch: 'full' },
      {
        path: 'general',
        title: `${TITLE}.STOREFRONT`,
        loadComponent: async () =>
          import(
            './pages/storefront-root/features/storefront-status/storefront-status.component'
          ).then((m) => m.StorefrontStatusComponent),
      },
      {
        path: 'address',
        title: `${TITLE}.STOREFRONT`,
        loadComponent: async () =>
          import(
            './pages/storefront-root/features/storefront-address/storefront-address.component'
          ).then((m) => m.StorefrontAddressComponent),
      },
    ],
  },
  {
    path: 'events',
    canActivate: [tenantSetupGuard, i18nGuard],
    data: {
      i18n: ['dashboard', 'event-root', 'onboarding', 'floor-types', 'locations'],
    },
    children: [
      {
        path: '',
        title: `${TITLE}.EVENTS`,
        loadComponent: async () =>
          import('./pages/event-root/event-root.component').then(
            (m) => m.EventRootComponent,
          ),
      },
      {
        path: 'create',
        title: `${TITLE}.EVENT_CREATE`,
        canActivate: [tenantWritableGuard],
        data: { writableFallback: '/events' },
        loadComponent: async () =>
          import('./pages/event-root/pages/create-event.component').then(
            (m) => m.CreateEventComponent,
          ),
      },
      {
        path: ':id/edit',
        title: `${TITLE}.EVENT_EDIT`,
        canActivate: [tenantWritableGuard, eventEditableGuard],
        data: { writableFallback: '/events' },
        loadComponent: async () =>
          import('./pages/event-root/pages/create-event.component').then(
            (m) => m.CreateEventComponent,
          ),
      },
      {
        path: ':id/manage',
        title: `${TITLE}.EVENT_MANAGE`,
        loadComponent: async () =>
          import('./pages/event-root/pages/manage-event.component').then(
            (m) => m.ManageEventComponent,
          ),
        children: [
          { path: '', pathMatch: 'full' as const, redirectTo: 'tickets' },
          {
            path: 'tickets',
            loadComponent: async () =>
              import(
                './pages/event-root/features/manage/tickets-tab.component'
              ).then((m) => m.TicketsTabComponent),
          },
          {
            path: 'scanners',
            loadComponent: async () =>
              import(
                './pages/event-root/features/manage/scanners-tab.component'
              ).then((m) => m.ScannersTabComponent),
          },
        ],
      },
    ],
  },
  {
    path: 'locations',
    canActivate: [tenantSetupGuard, i18nGuard],
    data: { i18n: ['locations', 'floor-types'] },
    children: [
      {
        path: '',
        title: `${TITLE}.LOCATIONS`,
        loadComponent: async () =>
          import('./pages/locations-root/locations-root.component').then(
            (m) => m.LocationsRootComponent,
          ),
      },
      {
        path: 'create',
        title: `${TITLE}.LOCATION_CREATE`,
        canActivate: [tenantWritableGuard],
        data: { writableFallback: '/locations' },
        loadComponent: async () =>
          import(
            './pages/locations-root/pages/location-form.component'
          ).then((m) => m.LocationFormComponent),
      },
      {
        path: ':id/edit',
        title: `${TITLE}.LOCATION_EDIT`,
        canActivate: [tenantWritableGuard],
        data: { writableFallback: '/locations' },
        loadComponent: async () =>
          import(
            './pages/locations-root/pages/location-form.component'
          ).then((m) => m.LocationFormComponent),
      },
    ],
  },
  {
    path: 'settings',
    canActivate: [AuthGuard, i18nGuard],
    data: { i18n: ['settings'] },
    loadComponent: async () =>
      import('./pages/settings/settings-root.component').then(
        (m) => m.SettingsRootComponent,
      ),
    children: [
      { path: '', redirectTo: settingsLandingTab, pathMatch: 'full' },
      {
        path: 'tenant',
        canActivate: [tenantSetupGuard],
        title: `${TITLE}.SETTINGS_TENANT`,
        loadComponent: async () =>
          import(
            './pages/settings/tenant-settings/tenant-settings.component'
          ).then((m) => m.TenantSettingsComponent),
      },
      {
        path: 'account',
        title: `${TITLE}.SETTINGS_ACCOUNT`,
        loadComponent: async () =>
          import(
            './pages/settings/account-settings/account-settings.component'
          ).then((m) => m.AccountSettingsComponent),
      },
      {
        path: 'payments',
        canActivate: [tenantSetupGuard],
        title: `${TITLE}.SETTINGS_PAYMENTS`,
        loadComponent: async () =>
          import(
            './pages/settings/payments-settings/payments-settings.component'
          ).then((m) => m.PaymentsSettingsComponent),
      },
      {
        path: 'security',
        title: `${TITLE}.SETTINGS_SECURITY`,
        loadComponent: async () =>
          import(
            './pages/settings/security-settings/security-settings.component'
          ).then((m) => m.SecuritySettingsComponent),
      },
      {
        path: 'activity',
        canActivate: [tenantSetupGuard],
        title: `${TITLE}.SETTINGS_ACTIVITY`,
        loadComponent: async () =>
          import(
            './pages/settings/activity-settings/activity-settings.component'
          ).then((m) => m.ActivitySettingsComponent),
      },
      {
        path: 'language',
        title: `${TITLE}.SETTINGS_LANGUAGE`,
        loadComponent: async () =>
          import(
            './pages/settings/language-settings/language-settings.component'
          ).then((m) => m.LanguageSettingsComponent),
      },
    ],
  },
  ...(environment.production
    ? []
    : [
        {
          path: 'dev',
          title: `${TITLE}.DEV_TOOLS`,
          loadComponent: async () =>
            import('./pages/dev-tools/dev-tools.component').then(
              (m) => m.DevToolsComponent,
            ),
          children: [
            { path: '', pathMatch: 'full' as const, redirectTo: 'feature-flags' },
            {
              path: 'logging',
              loadComponent: async () =>
                import('./pages/dev-tools/tabs/dev-logging.component').then(
                  (m) => m.DevLoggingComponent,
                ),
            },
            {
              path: 'feature-flags',
              loadComponent: async () =>
                import(
                  './pages/dev-tools/tabs/dev-feature-flags.component'
                ).then((m) => m.DevFeatureFlagsComponent),
            },
            {
              path: 'mails',
              loadComponent: async () =>
                import('./pages/dev-tools/tabs/dev-mails.component').then(
                  (m) => m.DevMailsComponent,
                ),
            },
            {
              path: 'checkin',
              loadComponent: async () =>
                import('./pages/dev-tools/tabs/dev-checkin.component').then(
                  (m) => m.DevCheckinComponent,
                ),
            },
            {
              path: 'payments-states',
              canActivate: [i18nGuard],
              data: { i18n: ['settings'] },
              loadComponent: async () =>
                import(
                  './pages/dev-tools/tabs/dev-payments-states.component'
                ).then((m) => m.DevPaymentsStatesComponent),
            },
            {
              path: 'styleguide',
              canActivate: [i18nGuard],
              data: { i18n: ['welcome-root', 'event-root', 'locations'] },
              loadComponent: async () =>
                import('./pages/styleguide/styleguide.component').then(
                  (m) => m.StyleguideComponent,
                ),
            },
          ],
        },
      ]),
];

export const routes: Routes = [
  {
    path: '',
    loadComponent: async () =>
      import('./layout/simple-layout/simple-layout.component').then(
        (m) => m.LayoutSimpleComponent,
      ),
    children: [...simpleLayoutRoutes, ...onboardingRoutes],
  },
  {
    path: '',
    loadComponent: async () =>
      import('./layout/main-layout/layout.component').then(
        (m) => m.LayoutComponent,
      ),
    canActivate: [AuthGuard, twoFactorSetupGuard, i18nGuard],
    data: { i18n: ['main-layout'] },
    children: [
      {
        path: '',
        children: [
          ...protectedRoutes,
          { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
        ],
      },
    ],
  },
  {
    path: 'forbidden',
    title: `${TITLE}.FORBIDDEN`,
    loadComponent: async () =>
      import('./pages/error-pages/forbidden/forbidden.component').then(
        (m) => m.ForbiddenComponent,
      ),
  },
  {
    path: 'server-error',
    title: `${TITLE}.SERVER_ERROR`,
    loadComponent: async () =>
      import('./pages/error-pages/server-error/server-error.component').then(
        (m) => m.ServerErrorComponent,
      ),
  },
  {
    path: '**',
    title: `${TITLE}.NOT_FOUND`,
    loadComponent: async () =>
      import('./pages/error-pages/not-found/not-found.component').then(
        (m) => m.NotFoundComponent,
      ),
  },
];
